/**
 * SIP Peer Schemas - Browser-Safe Exports
 * 
 * This file contains ONLY Zod schemas with ZERO Node.js dependencies.
 * It is safe to import in browser environments (React, Vite, etc.)
 * 
 * CRITICAL: Do NOT import anything from:
 * - '../sip/' (contains database operations)
 * - '../drizzle/' (database connection)
 * - '../dns-resolution' (Node.js dns module)
 * - Any other Node.js-only modules
 * 
 * @packageDocumentation
 */

import { z } from 'zod';
import { optionalEnum, optionalNullableUuid, nullableEnum } from './transforms';

// ========================================
// SUB-SCHEMAS (Browser-Safe)
// ========================================

/**
 * Media configuration sub-schema
 * Note: nullable().optional() allows both null and undefined from frontend
 */
function addMediaConfigIssue(ctx: z.RefinementCtx, path: Array<string | number>, message: string): void {
  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    message,
    path,
  });
}

export const MediaConfigSchema = z.object({
  sdpStripIce: z.boolean().nullable().optional(), // Strip ICE candidates from SDP (for carriers that don't support ICE)
  sdpOutboundProfile: z.enum(['passthrough', 'carrier-basic-rtp']).nullable().optional(),
  srtp: z.enum(['disabled', 'optional', 'mandatory']).nullable().optional(),
  cryptoSuites: z.array(z.string().min(1)).nullable().optional(),
  expandCryptoSuites: z.boolean().nullable().optional()
}).superRefine((data, ctx) => {
  if (data.srtp == null) {
    addMediaConfigIssue(ctx, ['srtp'], 'Explicit SRTP media policy is required for peers');
    return;
  }

  if (data.srtp !== 'disabled') {
    const cryptoSuites = data.cryptoSuites;
    if (!Array.isArray(cryptoSuites) || cryptoSuites.length === 0) {
      addMediaConfigIssue(ctx, ['cryptoSuites'], 'At least one SRTP crypto suite is required when SRTP is optional or mandatory');
    }
  }
}).nullable().optional();

/**
 * Authentication configuration sub-schema
 */
export const AuthConfigSchema = z.object({
  digestAuth: z.boolean().nullable().optional(),
  nonce: z.string().nullable().optional(),
  qop: z.string().nullable().optional(),
  algorithm: z.enum(['MD5', 'SHA-256']).nullable().optional()
}).nullable().optional();

/**
 * Routing configuration sub-schema with OPTIONS ping support
 */
const EndpointSelectionMethodSchema = z.enum(['round-robin', 'weighted', 'least-connections']);

const PeerEndpointSchema = z.object({
  ip: z.preprocess(
    (value) => value === '' ? null : value,
    z.string().trim().min(1).nullable().default(null),
  ),
  host: z.string().trim().min(1),
  port: z.number().min(1).max(65535),
  weight: z.number().min(0).max(100).optional(),
  enabled: z.boolean().optional().default(true),
});

export const RoutingConfigSchema = z.object({
  sourceIpRestrictions: z.array(z.string()).nullable().optional(),
  endpoints: z.array(PeerEndpointSchema).nullable().optional(),
  endpointSelectionMethod: EndpointSelectionMethodSchema.nullable().optional(),
  // OPTIONS keep-alive settings
  optionsPingEnabled: z.boolean().nullable().optional(),
  optionsPingInterval: z.number().min(5).max(3600).nullable().optional(),
  optionsPingTimeout: z.number().min(1).max(30).nullable().optional(),
  optionsPingFailureThreshold: z.number().min(1).max(10).nullable().optional(),
}).nullable().optional();

/**
 * Outbound TLS configuration sub-schema
 * Uses optionalEnum for minVersion to handle empty string from <select> elements
 */
export const OutboundTlsConfigSchema = z.object({
  certificateId: z.string().nullable().optional(),
  caCertificateId: z.string().nullable().optional(),
  verifyServer: z.boolean().nullable().optional(),
  // Use optionalEnum to convert empty string from <select> to undefined
  minVersion: optionalEnum(['TLSv1.2', 'TLSv1.3'] as const),
  ciphers: z.array(z.string()).nullable().optional(),
  sni: z.string().nullable().optional()
}).nullable().optional();

/**
 * Metadata sub-schema
 */
const OutboundBudgetWindowSchema = z.object({
  enabled: z.boolean(),
  windowMs: z.number().finite().int().positive().max(3_600_000),
  maxCalls: z.number().finite().int().nonnegative(),
  maxConcurrent: z.number().finite().int().nonnegative().max(2_000),
  maxDurationMs: z.number().finite().int().positive(),
  setupTimeoutMs: z.number().finite().int().positive(),
}).superRefine((value, ctx) => {
  if (value.setupTimeoutMs > value.maxDurationMs) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['setupTimeoutMs'], message: 'Setup timeout cannot exceed maximum duration' });
  }
});

export const OutboundBudgetSchema = z.object({
  global: OutboundBudgetWindowSchema,
  peer: OutboundBudgetWindowSchema,
  emergency: OutboundBudgetWindowSchema.optional(),
}).strict();

const SwedenGeographicPrefixSchema = z.object({
  // This is a user-reviewed prefix allowlist, not an automatically verified PTS dataset.
  prefix: z.string().regex(/^[1-9]\d{0,3}$/, 'Prefix must be canonical ASCII digits and cannot start with 0'),
  minNationalLength: z.number().finite().int().min(1).max(15),
  maxNationalLength: z.number().finite().int().min(1).max(15),
}).strict().superRefine((value, ctx) => {
  if (value.minNationalLength < value.prefix.length + 1) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['minNationalLength'], message: 'National length must include at least one subscriber digit' });
  }
  if (value.minNationalLength > value.maxNationalLength) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['minNationalLength'], message: 'Minimum national length cannot exceed maximum' });
  }
});

export const SwedenOutboundSchema = z.object({
  enabled: z.boolean(),
  source: z.literal('user-reviewed-prefix-allowlist'),
  sourceVersion: z.string().trim().min(1),
  geographicPrefixes: z.array(SwedenGeographicPrefixSchema).max(256),
  allowPremium: z.boolean(),
  allowedShortcodes: z.array(z.string().regex(/^\d{1,6}$/)).max(256),
  allowEmergency: z.boolean(),
}).strict().superRefine((value, ctx) => {
  const prefixes = value.geographicPrefixes.map((entry) => entry.prefix);
  if (new Set(prefixes).size !== prefixes.length) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['geographicPrefixes'], message: 'Duplicate geographic prefixes are not allowed' });
  }
  for (let index = 0; index < prefixes.length; index += 1) {
    const prefix = prefixes[index];
    if (prefix === undefined) continue;
    for (let other = index + 1; other < prefixes.length; other += 1) {
      const otherPrefix = prefixes[other];
      if (otherPrefix !== undefined && (prefix.startsWith(otherPrefix) || otherPrefix.startsWith(prefix))) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['geographicPrefixes'], message: 'Overlapping geographic prefixes are not allowed' });
      }
    }
  }
  // Never permit a broad geographic entry to absorb known Swedish mobile,
  // premium-rate, or freephone ranges. Exact reviewed entries can be added by
  // an operator, but a parent prefix (for example "9") is rejected.
  const reserved = ['20', '70', '72', '73', '76', '79', '90', '93', '94'];
  for (const prefix of prefixes) {
    if (reserved.some((range) => range.startsWith(prefix))) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['geographicPrefixes'], message: `Geographic prefix ${prefix} overlaps a reserved mobile, premium, or freephone range` });
    }
  }
});

export const MetadataSchema = z.object({
  description: z.string().optional(),
  tags: z.array(z.string()).optional(),
  environment: z.string().optional(),
  userAgent: z.string().refine((value) => !/[\r\n]/.test(value), 'User-Agent must not contain CR or LF').optional(),
  outboundMinSessionExpiresSeconds: z.number().int().min(90).optional(),
  minSessionExpiresSeconds: z.number().int().min(90).optional(),
  carrierId: z.string().optional(),
  outboundBudget: OutboundBudgetSchema.optional(),
  // Explicit, persistable outbound destination policy. Free-form tags are not
  // security policy and are intentionally not accepted for this purpose.
  swedenOutbound: SwedenOutboundSchema.optional(),
  teamsConfig: z.object({
    tenantId: z.string().optional(),
    sbcFqdn: z.string(),
    directRoutingDomains: z.array(z.string()),
    audioCodes: z.object({
      emulation: z.boolean(),
      version: z.string(),
      deviceModel: z.string()
    }),
    certificateRequirements: z.object({
      requireValidCert: z.boolean(),
      trustedRoots: z.array(z.string()),
      minimumKeySize: z.number()
    })
  }).optional()
}).passthrough().nullable().optional();

// ========================================
// SIP PROFILE SCHEMAS
// ========================================

const SipProfileSessionTimerModeSchema = z.enum(['disabled', 'supported', 'required']);
const SipProfileSessionRefresherPreferenceSchema = z.enum(['uas', 'uac', 'none']);
const SipProfileDigestQopSchema = z.enum(['auth', 'auth-int']);
const SipProfileDigestAlgorithmSchema = z.enum(['MD5', 'SHA-256']);
const SipProfileSrtpModeSchema = z.enum(['disabled', 'optional', 'mandatory']);
const SipProfileSdpOutboundProfileSchema = nullableEnum(['passthrough', 'carrier-basic-rtp']).optional();

type SipProfilePolicyInput = {
  sessionTimerMode?: z.infer<typeof SipProfileSessionTimerModeSchema> | null | undefined;
  sessionExpiresDefaultSeconds?: number | null | undefined;
  sessionExpiresMinSeconds?: number | null | undefined;
  sessionExpiresMaxSeconds?: number | null | undefined;
  sessionRefresherPreference?: z.infer<typeof SipProfileSessionRefresherPreferenceSchema> | null | undefined;
  sessionTimerRejectBelowMin?: boolean | null | undefined;
  registrarEnabled?: boolean | null | undefined;
  registerExpiresDefaultSeconds?: number | null | undefined;
  registerExpiresMinSeconds?: number | null | undefined;
  registerExpiresMaxSeconds?: number | null | undefined;
  registerMaxContacts?: number | null | undefined;
  registerAllowWildcardContact?: boolean | null | undefined;
  registerRequireContact?: boolean | null | undefined;
  digestAuthEnabled?: boolean | null | undefined;
  digestNonceLifetimeSeconds?: number | null | undefined;
  digestLockoutMaxAttempts?: number | null | undefined;
  digestLockoutWindowSeconds?: number | null | undefined;
  digestQop?: z.infer<typeof SipProfileDigestQopSchema> | null | undefined;
  digestAlgorithm?: z.infer<typeof SipProfileDigestAlgorithmSchema> | null | undefined;
  srtpMode?: z.infer<typeof SipProfileSrtpModeSchema> | null | undefined;
  sdpStripIce?: boolean | null | undefined;
  sdpOutboundProfile?: z.infer<typeof SipProfileSdpOutboundProfileSchema> | undefined;
};

function addSipProfileIssue(ctx: z.RefinementCtx, path: Array<string | number>, message: string): void {
  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    message,
    path,
  });
}

function validatePositiveTriple(
  ctx: z.RefinementCtx,
  values: {
    min: number | null | undefined;
    defaultValue: number | null | undefined;
    max: number | null | undefined;
  },
  paths: {
    min: Array<string | number>;
    defaultValue: Array<string | number>;
    max: Array<string | number>;
  },
  label: string,
  requireAll: boolean,
): void {
  const hasMin = values.min != null;
  const hasDefault = values.defaultValue != null;
  const hasMax = values.max != null;

  if (!hasMin && !hasDefault && !hasMax && !requireAll) {
    return;
  }

  if (!hasMin || !hasDefault || !hasMax) {
    if (requireAll) {
      if (!hasMin) {
        addSipProfileIssue(ctx, paths.min, `Required ${label} minimum value is missing`);
      }
      if (!hasDefault) {
        addSipProfileIssue(ctx, paths.defaultValue, `Required ${label} default value is missing`);
      }
      if (!hasMax) {
        addSipProfileIssue(ctx, paths.max, `Required ${label} maximum value is missing`);
      }
    } else {
      addSipProfileIssue(ctx, paths.min, `Provide all ${label} bounds together`);
      addSipProfileIssue(ctx, paths.defaultValue, `Provide all ${label} bounds together`);
      addSipProfileIssue(ctx, paths.max, `Provide all ${label} bounds together`);
    }
    return;
  }

  const min = values.min;
  const defaultValue = values.defaultValue;
  const max = values.max;
  if (min == null || defaultValue == null || max == null) {
    return;
  }

  if (min > defaultValue) {
    addSipProfileIssue(ctx, paths.min, `${label} minimum must be less than or equal to default`);
  }
  if (defaultValue > max) {
    addSipProfileIssue(ctx, paths.defaultValue, `${label} default must be less than or equal to maximum`);
  }
}

export function validateSipProfileSrtpCryptoSuites(
  srtpMode: z.infer<typeof SipProfileSrtpModeSchema> | null | undefined,
  cryptoSuiteCount: number,
): boolean {
  return srtpMode !== 'mandatory' || cryptoSuiteCount > 0;
}

export function validateSipProfilePolicy(data: SipProfilePolicyInput, ctx: z.RefinementCtx): void {
  validatePositiveTriple(
    ctx,
    {
      min: data.sessionExpiresMinSeconds,
      defaultValue: data.sessionExpiresDefaultSeconds,
      max: data.sessionExpiresMaxSeconds,
    },
    {
      min: ['sessionExpiresMinSeconds'],
      defaultValue: ['sessionExpiresDefaultSeconds'],
      max: ['sessionExpiresMaxSeconds'],
    },
    'session expiry',
    data.sessionTimerMode === 'required',
  );

  const registrarEnabled = data.registrarEnabled === true;
  validatePositiveTriple(
    ctx,
    {
      min: data.registerExpiresMinSeconds,
      defaultValue: data.registerExpiresDefaultSeconds,
      max: data.registerExpiresMaxSeconds,
    },
    {
      min: ['registerExpiresMinSeconds'],
      defaultValue: ['registerExpiresDefaultSeconds'],
      max: ['registerExpiresMaxSeconds'],
    },
    'REGISTER expires',
    registrarEnabled,
  );

  if (registrarEnabled && data.registerMaxContacts == null) {
    addSipProfileIssue(ctx, ['registerMaxContacts'], 'Registrar-enabled profiles require a maximum contact count');
  }
  if (registrarEnabled && data.registerAllowWildcardContact == null) {
    addSipProfileIssue(ctx, ['registerAllowWildcardContact'], 'Registrar-enabled profiles require an explicit wildcard contact policy');
  }
  if (registrarEnabled && data.registerRequireContact == null) {
    addSipProfileIssue(ctx, ['registerRequireContact'], 'Registrar-enabled profiles require an explicit contact requirement policy');
  }

  const digestEnabled = data.digestAuthEnabled === true;
  if (digestEnabled && data.digestNonceLifetimeSeconds == null) {
    addSipProfileIssue(ctx, ['digestNonceLifetimeSeconds'], 'Digest-auth-enabled profiles require a nonce lifetime');
  }
  if (digestEnabled && data.digestLockoutMaxAttempts == null) {
    addSipProfileIssue(ctx, ['digestLockoutMaxAttempts'], 'Digest-auth-enabled profiles require a lockout attempt limit');
  }
  if (digestEnabled && data.digestLockoutWindowSeconds == null) {
    addSipProfileIssue(ctx, ['digestLockoutWindowSeconds'], 'Digest-auth-enabled profiles require a lockout window');
  }
}

export const SipProfileCreateInputBaseSchema = z.object({
  name: z.string().min(1).max(255),
  description: z.string().nullable().optional(),
  enabled: z.boolean().default(true),
  sessionTimerMode: SipProfileSessionTimerModeSchema.default('supported'),
  sessionExpiresDefaultSeconds: z.number().int().positive().nullable().optional(),
  sessionExpiresMinSeconds: z.number().int().positive().nullable().optional(),
  sessionExpiresMaxSeconds: z.number().int().positive().nullable().optional(),
  sessionRefresherPreference: SipProfileSessionRefresherPreferenceSchema.nullable().optional(),
  sessionTimerRejectBelowMin: z.boolean().default(true),
  registrarEnabled: z.boolean().default(false),
  registerExpiresDefaultSeconds: z.number().int().positive().nullable().optional(),
  registerExpiresMinSeconds: z.number().int().positive().nullable().optional(),
  registerExpiresMaxSeconds: z.number().int().positive().nullable().optional(),
  registerMaxContacts: z.number().int().positive().nullable().optional(),
  registerAllowWildcardContact: z.boolean().default(false),
  registerRequireContact: z.boolean().default(true),
  digestAuthEnabled: z.boolean().default(false),
  digestNonceLifetimeSeconds: z.number().int().positive().nullable().optional(),
  digestLockoutMaxAttempts: z.number().int().positive().nullable().optional(),
  digestLockoutWindowSeconds: z.number().int().positive().nullable().optional(),
  digestQop: SipProfileDigestQopSchema.default('auth'),
  digestAlgorithm: SipProfileDigestAlgorithmSchema.default('SHA-256'),
  // MD5 is retained only for explicitly opted-in legacy profiles. This is an
  // input-only control; existing persisted MD5 profiles remain readable.
  legacyMd5OptIn: z.boolean().optional(),
  srtpMode: SipProfileSrtpModeSchema.default('optional'),
  sdpStripIce: z.boolean().default(false),
  sdpOutboundProfile: SipProfileSdpOutboundProfileSchema,
});

export const SipProfileCreateInputSchema = SipProfileCreateInputBaseSchema.superRefine((data, ctx) => {
  validateSipProfilePolicy(data, ctx);
  if (data.digestAlgorithm === 'MD5' && data.legacyMd5OptIn !== true) {
    addSipProfileIssue(ctx, ['legacyMd5OptIn'], 'MD5 requires explicit legacy opt-in');
  }
});

export const SipProfileUpdateInputSchema = SipProfileCreateInputBaseSchema.partial().superRefine(validateSipProfilePolicy);

export type SipProfileCreateInput = z.infer<typeof SipProfileCreateInputSchema>;
export type SipProfileUpdateInput = z.infer<typeof SipProfileUpdateInputSchema>;

// ========================================
// MAIN SCHEMAS (Browser-Safe)
// ========================================

const sipUriPattern = /^sip:[^@]+@[^@]+$/;

const optionalSipUriSchema = z.preprocess(
  (val) => (val === '' || val === null ? undefined : val),
  z.string().regex(sipUriPattern, 'Must be valid RFC 3261 SIP URI format (sip:user@domain)').optional()
);

/**
 * SIP Peer Create Input Base Schema - Object schema without refinements.
 * Use this when you need .omit() or .extend() - Zod cannot omit/extend on schemas with refinements.
 * For full validation use PeerCreateInputSchema.
 */
export const PeerCreateInputBaseSchema = z.object({
  // Required fields
  name: z.string().min(1).max(100),
  type: z.enum(['carrier', 'teams', 'webrtc', 'softphone', 'pbx']),
  sipUri: optionalSipUriSchema,
  domain: z.string().min(1).max(253),
  
  // Optional with defaults (transport derived from sip_interface.protocol)
  enabled: z.boolean().default(true),
  authRequired: z.boolean().default(false),
  registrationRequired: z.boolean().default(false),
  registrationPolicy: z.enum(['open', 'authenticated', 'closed']).default('open'),
  
  // Optional fields - nullable to accept null from frontend
  username: z.string().nullable().optional(),
  realm: z.string().nullable().optional(),
  registrationUri: z.string().regex(/^sip:[^@]+@[^@]+$/).nullable().optional(),
  registerExpiresDefaultSeconds: z.number().int().positive().nullable().optional(),
  registerExpiresMinSeconds: z.number().int().positive().nullable().optional(),
  registerExpiresMaxSeconds: z.number().int().positive().nullable().optional(),
  registerMaxContacts: z.number().int().positive().nullable().optional(),
  registerAllowWildcardContact: z.boolean().nullable().optional(),
  registerRequireContact: z.boolean().nullable().optional(),
  
  // Peer Group membership
  groupId: optionalNullableUuid,
  groupWeight: z.number().min(0).max(100).optional().default(100),
  groupPriority: z.number().min(1).max(100).optional().default(50),
  groupEnabled: z.boolean().optional().default(true),
  
  // Foreign keys - use optionalNullableUuid to accept empty string from <select>
  // sipInterfaceId is required for all peers (validated in superRefine)
  codecGroupId: optionalNullableUuid,
  sipProfileId: optionalNullableUuid,
  tlsContextId: optionalNullableUuid,
  sipInterfaceId: optionalNullableUuid,
  mediaRealmId: optionalNullableUuid,
  hostId: optionalNullableUuid,
  inboundManipulationId: optionalNullableUuid,
  outboundManipulationId: optionalNullableUuid,
  
  // Source IP filtering
  sourceIpRanges: z.array(z.string()).nullable().optional(),

  // Nested configurations
  // mediaConfig holds the peer's media security policy (SRTP, crypto suites, SDP handling).
  // The RTP port range is contributed by the media realm at read time; the rest is peer policy.
  mediaConfig: MediaConfigSchema.optional(),
  authConfig: AuthConfigSchema,
  routingConfig: RoutingConfigSchema,
  outboundTlsConfig: OutboundTlsConfigSchema,
  metadata: MetadataSchema
});

/**
 * Normalize sourceIpRanges to array for validation (handles form textarea string or API array).
 */
function sourceIpRangesToArray(
  val: string | string[] | null | undefined
): string[] {
  if (val == null) return [];
  if (Array.isArray(val)) return val.filter(Boolean);
  return String(val)
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function isValidIpv4(value: string): boolean {
  const parts = value.split('.');
  return parts.length === 4 && parts.every((part) => {
    if (!/^\d+$/.test(part)) return false;
    const num = Number(part);
    return num >= 0 && num <= 255 && String(num) === part;
  });
}

function isValidIpv4OrCidr(value: string): boolean {
  const [ip, prefix] = value.split('/');
  if (!ip || !isValidIpv4(ip)) return false;
  if (prefix === undefined) return true;
  if (!/^\d+$/.test(prefix)) return false;
  const prefixNum = Number(prefix);
  return prefixNum >= 0 && prefixNum <= 32;
}

function isValidHostname(value: string): boolean {
  const normalized = value.trim();
  if (normalized.length < 1 || normalized.length > 253) return false;
  return normalized.split('.').every((label) =>
    label.length >= 1
    && label.length <= 63
    && /^[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?$/.test(label)
  );
}

function addPeerIssue(ctx: z.RefinementCtx, path: Array<string | number>, message: string): void {
  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    message,
    path,
  });
}

/** Data shape accepted by peerCreateRefinements (schema output or form with sourceIpRanges as string | array). */
type PeerCreateRefinementData = z.output<typeof PeerCreateInputBaseSchema> & {
  sourceIpRanges?: string | string[] | null | undefined;
};

/**
 * Shared refinement logic for SIP peer create - SINGLE SOURCE OF TRUTH for business rules.
 * Used by PeerCreateInputSchema and by the frontend form schema (which has sourceIpRanges as string | array).
 */
export function peerCreateRefinements(
  data: PeerCreateRefinementData,
  ctx: z.RefinementCtx
): void {
  validatePeerFinalShape(data, ctx);
}

export function validatePeerFinalShape(
  data: PeerCreateRefinementData,
  ctx: z.RefinementCtx
): void {
  const enabled = data.enabled !== false;
  if (enabled && !data.sipInterfaceId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'SIP interface is required for all peers',
      path: ['sipInterfaceId'],
    });
  }
  if (enabled && !data.mediaRealmId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Media realm is required for all peers',
      path: ['mediaRealmId'],
    });
  }
  if (data.registrationRequired === true) {
    if (data.registerExpiresDefaultSeconds == null) {
      addPeerIssue(ctx, ['registerExpiresDefaultSeconds'], 'Registration-enabled peers require an explicit default REGISTER expires value');
    }
    if (data.registerExpiresMinSeconds == null) {
      addPeerIssue(ctx, ['registerExpiresMinSeconds'], 'Registration-enabled peers require an explicit minimum REGISTER expires value');
    }
    if (data.registerExpiresMaxSeconds == null) {
      addPeerIssue(ctx, ['registerExpiresMaxSeconds'], 'Registration-enabled peers require an explicit maximum REGISTER expires value');
    }
    if (data.registerMaxContacts == null) {
      addPeerIssue(ctx, ['registerMaxContacts'], 'Registration-enabled peers require an explicit maximum contact count');
    }
    if (data.registerAllowWildcardContact == null) {
      addPeerIssue(ctx, ['registerAllowWildcardContact'], 'Registration-enabled peers require an explicit wildcard contact policy');
    }
    if (data.registerRequireContact == null) {
      addPeerIssue(ctx, ['registerRequireContact'], 'Registration-enabled peers require an explicit contact requirement policy');
    }
  }
  const ranges = sourceIpRangesToArray(data.sourceIpRanges);
  ranges.forEach((range, index) => {
    if (!isValidIpv4OrCidr(range)) {
      addPeerIssue(ctx, ['sourceIpRanges', index], 'Source IP ranges must be valid IPv4 addresses or CIDR ranges');
    }
  });
  if (enabled && ['carrier', 'teams'].includes(data.type) && ranges.length === 0) {
    addPeerIssue(ctx, ['sourceIpRanges'], 'Source IP ranges are required for carrier and Teams peers');
  }
  if (data.routingConfig?.optionsPingEnabled === true && !data.sipUri) {
    addPeerIssue(ctx, ['sipUri'], 'SIP URI is required when OPTIONS ping is enabled');
  }
  // Mandatory match: at least one of source IP ranges or domain so the peer can be identified for authorization
  const hasSourceIpRanges = ranges.length > 0;
  const hasDomain = data.domain != null && String(data.domain).trim().length > 0;
  if (enabled && !hasSourceIpRanges && !hasDomain) {
    addPeerIssue(ctx, ['sourceIpRanges'], 'Add source IP ranges or domain so the peer can be identified (required for security)');
  }

  const mediaConfig = data.mediaConfig;
  if (mediaConfig?.srtp === 'optional' || mediaConfig?.srtp === 'mandatory') {
    const cryptoSuites = mediaConfig.cryptoSuites;
    if (!Array.isArray(cryptoSuites) || cryptoSuites.length === 0) {
      addPeerIssue(ctx, ['mediaConfig', 'cryptoSuites'], 'At least one SRTP crypto suite is required when SRTP is optional or mandatory');
    }
  }

  const endpoints = data.routingConfig?.endpoints;
  if (Array.isArray(endpoints)) {
    endpoints.forEach((endpoint, index) => {
      if (!isValidHostname(endpoint.host)) {
        addPeerIssue(ctx, ['routingConfig', 'endpoints', index, 'host'], 'Endpoint host must be a valid hostname');
      }
      if (endpoint.ip != null && !isValidIpv4(endpoint.ip)) {
        addPeerIssue(ctx, ['routingConfig', 'endpoints', index, 'ip'], 'Endpoint IP must be a valid IPv4 address');
      }
    });
  }

  if (enabled && ['carrier', 'teams'].includes(data.type)) {
    const enabledEndpoints = Array.isArray(endpoints)
      ? endpoints.filter((endpoint) => endpoint.enabled !== false)
      : [];
    if (enabledEndpoints.length === 0) {
      addPeerIssue(ctx, ['routingConfig', 'endpoints'], 'At least one enabled endpoint is required for enabled carrier and Teams peers');
    }
  }

  if (data.routingConfig?.optionsPingEnabled === true) {
    if (data.routingConfig.optionsPingInterval == null) {
      addPeerIssue(ctx, ['routingConfig', 'optionsPingInterval'], 'OPTIONS keepalive requires an explicit interval when enabled');
    }
    if (data.routingConfig.optionsPingTimeout == null) {
      addPeerIssue(ctx, ['routingConfig', 'optionsPingTimeout'], 'OPTIONS keepalive requires an explicit timeout when enabled');
    }
    if (data.routingConfig.optionsPingFailureThreshold == null) {
      addPeerIssue(ctx, ['routingConfig', 'optionsPingFailureThreshold'], 'OPTIONS keepalive requires an explicit failure threshold when enabled');
    }
  }

  if (enabled && data.type === 'teams') {
    const enabledEndpoints = Array.isArray(endpoints) ? endpoints.filter((endpoint) => endpoint.enabled !== false) : [];
    enabledEndpoints.forEach((endpoint, index) => {
      if (endpoint.port !== 5061) {
        addPeerIssue(ctx, ['routingConfig', 'endpoints', index, 'port'], 'Teams Direct Routing endpoints must use TLS port 5061');
      }
    });
    if (!data.metadata?.userAgent) {
      addPeerIssue(ctx, ['metadata', 'userAgent'], 'Teams peers require explicit AudioCodes-compatible User-Agent metadata');
    }
    if (!data.metadata?.teamsConfig?.sbcFqdn) {
      addPeerIssue(ctx, ['metadata', 'teamsConfig', 'sbcFqdn'], 'Teams peers require teamsConfig.sbcFqdn');
    }
    if (data.metadata?.teamsConfig?.audioCodes?.emulation !== true) {
      addPeerIssue(ctx, ['metadata', 'teamsConfig', 'audioCodes', 'emulation'], 'Teams peers require AudioCodes emulation metadata');
    }
  }

  if (enabled && data.type === 'carrier' && mediaConfig?.sdpOutboundProfile === 'carrier-basic-rtp') {
    if (data.metadata?.outboundMinSessionExpiresSeconds === undefined) {
      addPeerIssue(ctx, ['metadata', 'outboundMinSessionExpiresSeconds'], 'Carrier basic RTP profile requires explicit outbound Min-SE metadata');
    }
  }
}

/**
 * SIP Peer Create Input Schema - SINGLE SOURCE OF TRUTH
 * 
 * Used by:
 * - Backend API: zValidator('json', PeerCreateInputSchema)
 * - Frontend: form schema uses peerCreateRefinements() for same rules
 * 
 * The null→undefined cleanup happens in the domain operation.
 */
export const PeerCreateInputSchema = PeerCreateInputBaseSchema.superRefine(peerCreateRefinements);

/**
 * SIP Peer Update Input Schema - SINGLE SOURCE OF TRUTH
 * 
 * All fields optional for partial updates.
 * Separate from Create because required fields differ.
 */
export const PeerUpdateInputSchema = z.object({
  // All fields optional for partial updates
  name: z.string().min(1).max(100).optional(),
  type: z.enum(['carrier', 'teams', 'webrtc', 'softphone', 'pbx']).optional(),
  sipUri: z.preprocess(
    (val) => (val === '' || val === null ? undefined : val),
    z.string().regex(sipUriPattern, 'Must be valid RFC 3261 SIP URI format').optional()
  ),
  domain: z.string().min(1).max(253).optional(),
  enabled: z.boolean().optional(),
  authRequired: z.boolean().optional(),
  registrationRequired: z.boolean().optional(),
  registrationPolicy: z.enum(['open', 'authenticated', 'closed']).optional(),
  
  // Optional fields - nullable to accept null from frontend
  username: z.string().nullable().optional(),
  realm: z.string().nullable().optional(),
  registrationUri: z.string().regex(/^sip:[^@]+@[^@]+$/).nullable().optional(),
  registerExpiresDefaultSeconds: z.number().int().positive().nullable().optional(),
  registerExpiresMinSeconds: z.number().int().positive().nullable().optional(),
  registerExpiresMaxSeconds: z.number().int().positive().nullable().optional(),
  registerMaxContacts: z.number().int().positive().nullable().optional(),
  registerAllowWildcardContact: z.boolean().nullable().optional(),
  registerRequireContact: z.boolean().nullable().optional(),
  
  // Peer Group membership
  groupId: optionalNullableUuid,
  groupWeight: z.number().min(0).max(100).optional(),
  groupPriority: z.number().min(1).max(100).optional(),
  groupEnabled: z.boolean().optional(),
  
  // Foreign keys - use optionalNullableUuid to accept empty string from <select>
  codecGroupId: optionalNullableUuid,
  sipProfileId: optionalNullableUuid,
  tlsContextId: optionalNullableUuid,
  sipInterfaceId: optionalNullableUuid,
  mediaRealmId: optionalNullableUuid,
  hostId: optionalNullableUuid,
  inboundManipulationId: optionalNullableUuid,
  outboundManipulationId: optionalNullableUuid,
  
  // Source IP filtering
  sourceIpRanges: z.array(z.string()).nullable().optional(),

  // Nested configurations (mediaConfig holds peer media security policy: SRTP, crypto suites)
  mediaConfig: MediaConfigSchema.optional(),
  authConfig: AuthConfigSchema,
  routingConfig: RoutingConfigSchema,
  outboundTlsConfig: OutboundTlsConfigSchema,
  metadata: MetadataSchema
}).superRefine((data, ctx) => {
  if (data.type && ['carrier', 'teams'].includes(data.type) && (!data.sourceIpRanges || data.sourceIpRanges.length === 0)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Source IP ranges are required for carrier and Teams peers',
      path: ['sourceIpRanges'],
    });
  }
  if (data.sipInterfaceId !== undefined && !data.sipInterfaceId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'SIP interface cannot be cleared; it is required for all peers',
      path: ['sipInterfaceId'],
    });
  }
  if (data.mediaRealmId !== undefined && !data.mediaRealmId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Media realm cannot be cleared; it is required for all peers',
      path: ['mediaRealmId'],
    });
  }
  if (data.registrationRequired === true) {
    if (data.registerExpiresDefaultSeconds == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Registration-enabled peers require an explicit default REGISTER expires value',
        path: ['registerExpiresDefaultSeconds'],
      });
    }
    if (data.registerExpiresMinSeconds == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Registration-enabled peers require an explicit minimum REGISTER expires value',
        path: ['registerExpiresMinSeconds'],
      });
    }
    if (data.registerExpiresMaxSeconds == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Registration-enabled peers require an explicit maximum REGISTER expires value',
        path: ['registerExpiresMaxSeconds'],
      });
    }
    if (data.registerMaxContacts == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Registration-enabled peers require an explicit maximum contact count',
        path: ['registerMaxContacts'],
      });
    }
    if (data.registerAllowWildcardContact == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Registration-enabled peers require an explicit wildcard contact policy',
        path: ['registerAllowWildcardContact'],
      });
    }
    if (data.registerRequireContact == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Registration-enabled peers require an explicit contact requirement policy',
        path: ['registerRequireContact'],
      });
    }
  }
});

// ========================================
// TYPE DEFINITIONS (Browser-Safe)
// ========================================

/**
 * Type for creating a SIP Peer
 * Explicit definition for clean TypeScript ergonomics
 */
export type PeerCreateInput = {
  // Required fields
  name: string;
  type: 'carrier' | 'teams' | 'webrtc' | 'softphone' | 'pbx';
  sipUri?: string;
  domain: string;
  
  // Optional fields (transport derived from sip_interface.protocol)
  enabled?: boolean;
  authRequired?: boolean;
  registrationRequired?: boolean;
  registrationPolicy?: 'open' | 'authenticated' | 'closed';
  username?: string | null;
  realm?: string | null;
  registrationUri?: string | null;
  registerExpiresDefaultSeconds?: number | null;
  registerExpiresMinSeconds?: number | null;
  registerExpiresMaxSeconds?: number | null;
  registerMaxContacts?: number | null;
  registerAllowWildcardContact?: boolean | null;
  registerRequireContact?: boolean | null;
  groupId?: string | null;
  groupWeight?: number;
  groupPriority?: number;
  groupEnabled?: boolean;
  codecGroupId?: string | null;
  sipProfileId?: string | null;
  sipInterfaceId?: string | null; // Required for all peers (validated by superRefine)
  mediaRealmId?: string | null;
  hostId?: string | null;
  inboundManipulationId?: string | null;
  outboundManipulationId?: string | null;
  sourceIpRanges?: string[] | null;
  mediaConfig?: z.input<typeof MediaConfigSchema>;
  authConfig?: z.input<typeof AuthConfigSchema>;
  routingConfig?: z.input<typeof RoutingConfigSchema>;
  outboundTlsConfig?: z.input<typeof OutboundTlsConfigSchema>;
  metadata?: z.input<typeof MetadataSchema>;
};

/**
 * Type for updating a SIP Peer
 * All fields optional for partial updates
 */
export type PeerUpdateInput = Partial<PeerCreateInput>;

// ========================================
// SIP CODEC GROUP SCHEMAS (Browser-Safe)
// ========================================

export const CodecGroupCodecSchema = z.object({
  codecId: z.string(),
  codecName: z.string(),
  codecType: z.enum(['audio', 'video', 'fax']),
  priority: z.number(),
  enabled: z.boolean(),
  parameters: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).optional(),
});

/**
 * SIP Codec Group Create Input Schema - Browser-safe single source for UI forms.
 */
export const CodecGroupCreateInputSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  enabled: z.boolean().default(true),
  codecs: z.array(CodecGroupCodecSchema).default([]),
  dtmfMode: z.enum(['rfc2833', 'sip-info', 'inband']).default('rfc2833'),
  ptime: z.number().int().positive().optional(),
  isDefault: z.boolean().default(false),
});

/**
 * SIP Codec Group Update Input Schema - Browser-safe partial updates.
 */
export const CodecGroupUpdateInputSchema = z.object({
  name: z.string().optional(),
  description: z.string().optional(),
  enabled: z.boolean().optional(),
  codecs: z.array(CodecGroupCodecSchema).optional(),
  dtmfMode: z.enum(['rfc2833', 'sip-info', 'inband']).optional(),
  ptime: z.number().int().positive().nullable().optional(),
  isDefault: z.boolean().optional(),
});

export type CodecGroupCreateInput = z.infer<typeof CodecGroupCreateInputSchema>;
export type CodecGroupUpdateInput = z.infer<typeof CodecGroupUpdateInputSchema>;

// ========================================
// SIP MANIPULATION SCHEMAS (Browser-Safe)
// ========================================

export const ManipulationTargetSchema = z.enum([
  'From',
  'To',
  'Request-URI',
  'P-Asserted-Identity',
  'Remote-Party-ID',
  'Contact',
  'Via',
]);

export const ManipulationFieldSchema = z.enum(['user', 'host', 'display', 'full']);
export const ManipulationActionSchema = z.enum(['replace', 'prefix', 'suffix', 'strip', 'set']);
export const ManipulationTypeSchema = z.enum(['header', 'uri']);

function isValidRegexPattern(pattern: string): boolean {
  try {
    new RegExp(pattern);
    return true;
  } catch {
    return false;
  }
}

export const ManipulationConditionSchema = z.object({
  target: ManipulationTargetSchema,
  field: ManipulationFieldSchema,
  pattern: z.string(),
});

export const ManipulationRuleSchema = z.object({
  priority: z.number().int().min(0),
  type: ManipulationTypeSchema,
  target: ManipulationTargetSchema,
  field: ManipulationFieldSchema,
  action: ManipulationActionSchema,
  pattern: z.string(),
  value: z.string(),
  description: z.string().optional(),
  enabled: z.boolean().optional().default(true),
  condition: ManipulationConditionSchema.optional(),
}).superRefine((rule, ctx) => {
  if (rule.action !== 'set' && rule.pattern.trim() === '') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Pattern is required',
      path: ['pattern'],
    });
  }

  if (rule.pattern.trim() !== '' && !isValidRegexPattern(rule.pattern)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Invalid regex pattern',
      path: ['pattern'],
    });
  }

  if (rule.action !== 'strip' && rule.value.trim() === '') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Value is required',
      path: ['value'],
    });
  }

  if (rule.condition) {
    if (rule.condition.pattern.trim() === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Condition pattern is required',
        path: ['condition', 'pattern'],
      });
    } else if (!isValidRegexPattern(rule.condition.pattern)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Invalid regex pattern',
        path: ['condition', 'pattern'],
      });
    }
  }
});

export const ManipulationCreateInputSchema = z.object({
  name: z.string().trim().min(1, 'Name is required'),
  description: z.string().optional(),
  enabled: z.boolean().optional().default(true),
  rules: z.array(ManipulationRuleSchema).optional().default([]),
});

export const ManipulationUpdateInputSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').optional(),
  description: z.string().nullable().optional(),
  enabled: z.boolean().optional(),
  rules: z.array(ManipulationRuleSchema).optional(),
});

export type ManipulationTarget = z.infer<typeof ManipulationTargetSchema>;
export type ManipulationField = z.infer<typeof ManipulationFieldSchema>;
export type ManipulationAction = z.infer<typeof ManipulationActionSchema>;
export type ManipulationType = z.infer<typeof ManipulationTypeSchema>;
export type ManipulationCondition = z.infer<typeof ManipulationConditionSchema>;
export type ManipulationRuleInput = z.infer<typeof ManipulationRuleSchema>;
export type ManipulationCreateInput = z.infer<typeof ManipulationCreateInputSchema>;
export type ManipulationUpdateInput = z.infer<typeof ManipulationUpdateInputSchema>;
